import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ProviderLayout } from '@/components/layouts/provider-layout';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Zap, 
  CreditCard,
  Package,
  Plus,
  Send,
  FileText,
  BarChart3,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUpRight,
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Sample data for charts
const revenueData = [
  { date: 'Jan 1', revenue: 1200 },
  { date: 'Jan 7', revenue: 1800 },
  { date: 'Jan 14', revenue: 2400 },
  { date: 'Jan 21', revenue: 2200 },
  { date: 'Jan 28', revenue: 2800 },
  { date: 'Feb 4', revenue: 3200 },
  { date: 'Feb 11', revenue: 3500 },
];

const operationsData = [
  { date: 'Jan 1', apiCalls: 45000, automationRuns: 15000 },
  { date: 'Jan 7', apiCalls: 52000, automationRuns: 18000 },
  { date: 'Jan 14', apiCalls: 48000, automationRuns: 22000 },
  { date: 'Jan 21', apiCalls: 55000, automationRuns: 25000 },
  { date: 'Jan 28', apiCalls: 62000, automationRuns: 28000 },
  { date: 'Feb 4', apiCalls: 68000, automationRuns: 32000 },
  { date: 'Feb 11', apiCalls: 72000, automationRuns: 35000 },
];


const topConsumers = [
  { name: 'Digital Marketing Pro', usage: 245000, revenue: 1234.50, growth: 12.5, avatar: 'DM' },
  { name: 'CRM Automation Ltd', usage: 189000, revenue: 987.20, growth: 8.3, avatar: 'CA' },
  { name: 'E-Commerce Tools', usage: 156000, revenue: 823.10, growth: -2.1, avatar: 'EC' },
  { name: 'Data Analytics Inc', usage: 134000, revenue: 745.30, growth: 15.7, avatar: 'DA' },
  { name: 'Social Media Suite', usage: 98000, revenue: 512.80, growth: 5.4, avatar: 'SM' },
];

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Calculate total operations
  const totalOperations = '1.87M';
  const operationsGrowth = 18.7;
  const operationsBreakdown = {
    api: 67,
    automation: 33,
  };
  
  return (
    <ProviderLayout>
      <div className="space-y-8 p-8">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold">{t('dashboard.title')}</h1>
          <p className="text-muted-foreground">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Total Revenue */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.totalRevenue')}</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">$24,389.50</div>
              <div className="flex items-center text-xs">
                <TrendingUp className="mr-1 h-3 w-3 text-green-500" />
                <span className="text-green-500 font-medium">+12.5%</span>
                <span className="text-muted-foreground ml-1">{t('dashboard.fromLastMonth')}</span>
              </div>
              <Progress value={72} className="mt-2 h-1" />
            </CardContent>
          </Card>

          {/* Total Operations */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.totalOperations')}</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalOperations}</div>
              <div className="flex items-center text-xs">
                <TrendingUp className="mr-1 h-3 w-3 text-green-500" />
                <span className="text-green-500 font-medium">+{operationsGrowth}%</span>
                <span className="text-muted-foreground ml-1">{t('dashboard.fromLastMonth')}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-1" />
                  <span className="text-muted-foreground">
                    {t('dashboard.operationsBreakdown.api', {
                      percent: operationsBreakdown.api,
                    })}
                  </span>
                </div>
                <div className="flex items-center text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500 mr-1" />
                  <span className="text-muted-foreground">
                    {t('dashboard.operationsBreakdown.automation', {
                      percent: operationsBreakdown.automation,
                    })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Customers */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.activeCustomers')}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">128</div>
              <div className="flex items-center text-xs">
                <TrendingUp className="mr-1 h-3 w-3 text-green-500" />
                <span className="text-green-500 font-medium">+4.2%</span>
                <span className="text-muted-foreground ml-1">{t('dashboard.fromLastMonth')}</span>
              </div>
              <div className="flex items-center gap-1 mt-2">
                <Badge variant="secondary" className="text-xs">{t('dashboard.subscribed', { count: 89 })}</Badge>
                <Badge variant="outline" className="text-xs">{t('dashboard.credits', { count: 39 })}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* API Products */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.apiProducts')}</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">12</div>
              <div className="flex items-center text-xs">
                <CheckCircle className="mr-1 h-3 w-3 text-green-500" />
                <span className="text-muted-foreground">{t('dashboard.healthy', { count: 10 })}</span>
                <AlertCircle className="ml-2 mr-1 h-3 w-3 text-yellow-500" />
                <span className="text-muted-foreground">{t('dashboard.warnings', { count: 2 })}</span>
              </div>
              <Button variant="link" className="px-0 h-auto mt-1 text-xs" onClick={() => navigate('/provider/products')}>
                {t('dashboard.manageProducts')}
                <ArrowUpRight className="ml-1 h-3 w-3" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Revenue Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {t('dashboard.revenueTrend')}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/provider/analytics/revenue')}
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  {t('dashboard.details')}
                </Button>
              </CardTitle>
              <CardDescription>
                {t('dashboard.dailyRevenue')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: number) => [`$${value}`, 'Revenue']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#3b82f6" 
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Operations Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {t('dashboard.operationsTrend')}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/provider/analytics/usage')}
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  {t('dashboard.details')}
                </Button>
              </CardTitle>
              <CardDescription>
                {t('dashboard.apiCallsAndAutomation')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={operationsData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `${value / 1000}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value: number) => [`${value.toLocaleString()}`, '']}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="line"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="apiCalls" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="API Calls"
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="automationRuns" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    name="Automation Runs"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid gap-4 md:grid-cols-3">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.quickActions')}</CardTitle>
              <CardDescription>
                {t('dashboard.commonTasks')}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2">
              <Button className="w-full justify-start" onClick={() => navigate('/provider/products/new')}>
                <Plus className="mr-2 h-4 w-4" />
                {t('dashboard.createApiProduct')}
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/provider/customers/invite')}>
                <Send className="mr-2 h-4 w-4" />
                {t('dashboard.inviteCustomer')}
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/provider/docs/getting-started')}>
                <FileText className="mr-2 h-4 w-4" />
                {t('dashboard.updateDocumentation')}
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/provider/billing/stripe')}>
                <Zap className="mr-2 h-4 w-4" />
                {t('dashboard.configureStripe')}
              </Button>
            </CardContent>
          </Card>

          {/* Top Consumers */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {t('dashboard.topConsumers')}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/provider/customers')}
                >
                  {t('dashboard.viewAll')}
                  <ArrowUpRight className="ml-1 h-3 w-3" />
                </Button>
              </CardTitle>
              <CardDescription>
                {t('dashboard.highestUsageCustomers')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topConsumers.map((consumer, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="text-xs font-medium">
                        {consumer.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">{consumer.name}</p>
                        <span className="text-sm font-medium">${consumer.revenue}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          {consumer.usage.toLocaleString()} {t('dashboard.operations')}
                        </p>
                        <div className="flex items-center text-xs">
                          {consumer.growth > 0 ? (
                            <>
                              <TrendingUp className="mr-1 h-3 w-3 text-green-500" />
                              <span className="text-green-500">+{consumer.growth}%</span>
                            </>
                          ) : (
                            <>
                              <TrendingDown className="mr-1 h-3 w-3 text-red-500" />
                              <span className="text-red-500">{consumer.growth}%</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status & Alerts */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.statusAlerts')}</CardTitle>
            <CardDescription>
              {t('dashboard.systemHealth')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{t('dashboard.allSystemsOperational')}</p>
                  <p className="text-xs text-muted-foreground">{t('dashboard.apiGatewayNormal')}</p>
                </div>
                <Badge variant="outline" className="text-xs">
                  <Clock className="mr-1 h-3 w-3" />
                  {t('common.minAgo', { count: 2 })}
                </Badge>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{t('dashboard.highUsageWeatherApi')}</p>
                  <p className="text-xs text-muted-foreground">{t('dashboard.considerScaling')}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/provider/products/weather-api')}>
                  {t('dashboard.viewProduct')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ProviderLayout>
  );
}
